import {
    CONDICOES,
    type CondicaoId,
    isCondicaoAlcance,
    isCondicaoExecucao,
} from '@acme/core'
import { FormFieldSwitchSelect } from 'global/components/Form'
import Grid from 'global/components/ui/Grid'
import { useWatch } from 'react-hook-form'
import { z } from 'zod'
import { CONDICOES_UI } from '../condicoes'
import { CatalogoDialog, SeloIcone } from './CatalogoDialog'

const OPCOES_POLARIDADE = [
    { value: true, label: 'ocorrer' },
    { value: false, label: 'não ocorrer' },
] as const

const OPCOES_INCLUSAO = [
    { value: true, label: 'incluir' },
    { value: false, label: 'exceto' },
] as const

const schema = z
    .object({
        condicaoId: z.enum(
            Object.keys(CONDICOES) as [CondicaoId, ...CondicaoId[]],
        ),
        ocorre: z.boolean(),
        parametros: z.record(z.unknown()),
    })
    .superRefine((condicao, ctx) => {
        const resultado = CONDICOES[condicao.condicaoId].parametros.safeParse(
            condicao.parametros,
        )
        if (resultado.success) return
        for (const issue of resultado.error.issues)
            ctx.addIssue({ ...issue, path: ['parametros', ...issue.path] })
    })

export type Condicao = z.infer<typeof schema>

interface CondicaoDialogProps {
    /** Condição em edição; ausente, o diálogo cria uma nova. */
    valor?: Condicao
    onClose: () => void
    onSalvar: (condicao: Condicao) => void
}

/**
 * Sub-diálogo de uma condição da rotina: escolhe no catálogo e depois
 * configura. Salvar só devolve a condição pelo `onSalvar`.
 */
export function CondicaoDialog({
    valor,
    onClose,
    onSalvar,
}: CondicaoDialogProps) {
    return (
        <CatalogoDialog
            titulo={valor ? 'Editar condição' : 'Adicionar condição'}
            item="condição"
            cor="warning"
            catalogo={CONDICOES}
            ui={CONDICOES_UI}
            name="condicaoId"
            iniciar={(id: CondicaoId) => ({
                condicaoId: id,
                ocorre: true,
                parametros: CONDICOES_UI[id].parametrosIniciais,
            })}
            schema={schema}
            valor={valor}
            onClose={onClose}
            onSubmit={(condicao: Condicao) =>
                onSalvar({
                    condicaoId: condicao.condicaoId,
                    ocorre: condicao.ocorre,
                    parametros: condicao.parametros,
                })
            }
        >
            <ConfiguracaoCondicao />
        </CatalogoDialog>
    )
}

function ConfiguracaoCondicao() {
    const condicaoId = useWatch<Condicao, 'condicaoId'>({ name: 'condicaoId' })
    const { Parametros } = CONDICOES_UI[condicaoId]

    return (
        <Grid container>
            {!isCondicaoExecucao(condicaoId) && (
                <Grid item xs={12}>
                    <FormFieldSwitchSelect
                        name="ocorre"
                        label={
                            isCondicaoAlcance(condicaoId)
                                ? 'Funcionários'
                                : 'Ocorre'
                        }
                        options={
                            isCondicaoAlcance(condicaoId)
                                ? OPCOES_INCLUSAO
                                : OPCOES_POLARIDADE
                        }
                    />
                </Grid>
            )}
            {Parametros && (
                <Grid item xs={12}>
                    <Parametros />
                </Grid>
            )}
        </Grid>
    )
}

/** Selo com o ícone da condição, usado na lista da rotina. */
export function IconeCondicao({ condicaoId }: { condicaoId: CondicaoId }) {
    return <SeloIcone icone={CONDICOES_UI[condicaoId].icone} cor="warning" />
}
